import { useMelwaysInfo } from '@seek/melways-react';
import type { Market } from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import { Box, Checkbox, Stack } from 'braid-design-system';

import { useGetSuggestionsForRoleTitleLazyQuery } from 'src/graphql/Queries/getSuggestionsForRoleTitle.generated';

import { DEFAULT_SUGGESTION_COUNT } from '../../constants';
import type { Suggestion } from '../../types';
import { FilterAccordion } from '../AccordionFilter/FilterAccordion';
import { Filter } from '../Filter/Filter';

import translations from './.vocab';

type RoleTitleFIlterProps = {
  expanded: boolean;
  selectedMarket: Market;
  selectedItems: Suggestion[];
  onRoleTitleChange: (newSelectedItems: Suggestion[]) => void;
  onExpandedToggle: (expanded: boolean) => void;
  matchLatestRoleTitle: boolean;
  onMatchLatestRoleTitleChange: (newState: boolean) => void;
};

export const RoleTitleFilter = ({
  expanded,
  selectedMarket,
  selectedItems,
  onRoleTitleChange,
  onExpandedToggle,
  matchLatestRoleTitle,
  onMatchLatestRoleTitleChange,
}: RoleTitleFIlterProps) => {
  const { t } = useTranslations(translations);
  const { language } = useMelwaysInfo();

  const [triggerGetSuggestions, { data }] =
    useGetSuggestionsForRoleTitleLazyQuery({
      // Context: role title suggestions can have ones with different normalised text but the same ids
      // e.g. User Experience and Interface Designer has id of http://graph.seek.com/role-title/ux--ui-designer
      // Lead User Experience and Interface Designer also has the id of http://graph.seek.com/role-title/ux--ui-designer
      // Issue: Sharing the same ids causes Apollo to reuse `User Experience and Interface Designer` for Lead User Experience and Interface Designer as sharing the same id
      // which causes inconsistency
      // Solution: not cache this query at all.
      fetchPolicy: 'no-cache',
    });

  const suggestions = (data?.ontologyRoleTitlesSeniority || []).map(
    ({ id, normalisedText }) => ({
      id,
      name: normalisedText,
    }),
  );

  const handleSearchTextChange = (newSearchText: string) => {
    if (newSearchText) {
      triggerGetSuggestions({
        variables: {
          query: newSearchText,
          count: DEFAULT_SUGGESTION_COUNT,
          countryCode: selectedMarket,
          languageCode: language,
          session: '',
        },
      });
    }
  };

  return (
    <FilterAccordion
      id="role-title-accordion-filter"
      expanded={expanded}
      label={t('ROLE_TITLE')}
      selectedItems={selectedItems}
      onToggle={onExpandedToggle}
    >
      <Stack space="small">
        <Filter
          id="role-title-filter"
          label=""
          placeholder={t('ENTER_ROLE_TITLE_EG_MARKETING_MANAGER')}
          suggestions={suggestions}
          selectedItems={selectedItems}
          onDebouncedSearchTextChange={handleSearchTextChange}
          onChange={onRoleTitleChange}
        />
        <Box paddingLeft="xxsmall">
          <Checkbox
            size="small"
            id="role-title-filter-checkbox"
            label={t('MATCH_WITH_LATEST_ROLE_TITLE_ONLY')}
            checked={matchLatestRoleTitle}
            onChange={() => onMatchLatestRoleTitleChange(!matchLatestRoleTitle)}
          />
        </Box>
      </Stack>
    </FilterAccordion>
  );
};
